<template>
  <div class="debug-tools-container">
    <div class="cyber-grid"></div>
    <div class="cyber-glow"></div>
    
    <!-- 页面头部区域 - 单行布局 -->
    <div class="top-header-bar">
      <!-- 左侧: 标题 -->
      <div class="page-header">
        <div class="cyber-line"></div>
        <div class="title-content">
          <h1 class="page-title">调试/配置工具箱</h1>
          <div class="subtitle">Debug & Config Toolbox</div>
        </div>
      </div>
      
      <!-- 中间: 当前机器人徽章 -->
      <div class="selected-robot-badge" v-if="selectedRobot">
        <Icon type="md-options" />
        <span class="badge-label">当前:</span>
        <span class="badge-value">{{ selectedRobot.name }}</span>
      </div>
      
      <!-- 右侧: 机器人选择器 -->
      <Spin v-if="loadingRobots" class="robot-loading">
        <div class="cyber-loader">
          <div class="cyber-loader-inner"></div>
          <div class="cyber-loader-text">加载中...</div>
        </div>
      </Spin>
      
      
      <div v-else class="robot-selection-container">
        <button class="nav-btn prev" @click="scrollRobots('left')" v-if="robots.length > 3">
          <div class="arrow-3d">◀</div>
        </button>
        
        <!-- 搜索框 -->
        <div class="search-container" :class="{ expanded: searchExpanded }">
          <button class="search-btn" @click="toggleSearch">
            <span class="search-icon"></span>
          </button>
          <input 
            v-if="searchExpanded"
            ref="searchInput"
            v-model="searchQuery"
            type="text"
            class="search-input"
            placeholder="搜索机器人..."
            @blur="handleSearchBlur"
          />
          <button v-if="searchQuery" class="clear-btn" @click="clearSearch">
            <span class="close-icon"></span>
          </button>
        </div>
        
        <div class="robot-selector">
          <div class="selector-bg"></div>
          <div class="selector-inner" ref="selectorInner">
            <div 
              v-for="robot in filteredRobots" 
              :key="robot.id"
              class="robot-item"
              :class="{ 
                'active': selectedRobot && selectedRobot.code === robot.code,
                'online': robot.status === 'online',
                'offline': robot.status === 'offline'
              }"
              @click="selectRobot(robot)"
            >
              <div class="hexagon-bg"></div>
              <div class="robot-content">
                <div class="robot-avatar">
                  <div class="avatar-ring"></div>
                  <img :src="require('@/assets/images/voice/Robot-icon.png')" alt="Robot" class="robot-icon">
                  <span class="status-dot"></span>
                </div>
                <div class="robot-info">
                  <span class="robot-name">{{ robot.name }}</span>
                  <div class="status-line">
                    <span class="robot-code">{{ robot.code }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <button class="nav-btn next" @click="scrollRobots('right')" v-if="robots.length > 3">
          <div class="arrow-3d">▶</div>
        </button>
      </div>
    </div>
    
    <!-- 工具菜单区域 -->
    <div class="tools-section" v-if="!currentTool">
      
      <div class="function-grid">
        <div 
          v-for="tool in debugTools" 
          :key="tool.key"
          class="function-card"
          @click="selectTool(tool)"
          :class="{ disabled: tool.requireRobot && !selectedRobot }"
        >
          <div class="card-bg"></div>
          <div class="card-content">
            <div class="function-icon">
              <Icon :type="tool.icon" />
            </div>
            <h3 class="function-title">{{ tool.title }}</h3>
            <p class="function-desc">{{ tool.description }}</p>
            <div class="function-badge" v-if="tool.badge">
              {{ tool.badge }}
            </div>
            <div class="require-robot-tip" v-if="tool.requireRobot && !selectedRobot">
              需要先选择机器人
            </div>
          </div>
          <div class="card-corner top-right"></div>
          <div class="card-corner bottom-left"></div>
        </div>
      </div>
    </div>
    
    <!-- 工具显示区域 -->
    <div class="tool-display-section" v-else>
      <div class="tool-header">
        <div class="tool-title">
          <Icon :type="currentTool.icon" />
          <span>{{ currentTool.title }}</span>
        </div>
        <Button @click="backToToolSelection" class="back-btn" size="large">
          <Icon type="md-arrow-back" />
          返回工具选择
        </Button>
      </div>
      
      <div class="tool-content">
        <component 
          :is="currentTool.component" 
          :robotCode="selectedRobot ? selectedRobot.code : ''"
          :key="currentTool.requireRobot ? (selectedRobot ? selectedRobot.code : 'no-robot') : currentTool.key"
        />
      </div>
    </div>
    
    <!-- 角落装饰 -->
    <div class="corner-decoration top-left"></div>
    <div class="corner-decoration top-right"></div>
    <div class="corner-decoration bottom-left"></div>
    <div class="corner-decoration bottom-right"></div>
  </div>
</template>

<script>
import api from "@/api";
import NetworkDebug from "./NetworkDebug/index.vue";
import MonitorDict from "./MonitorDict/index.vue";
import DeviceExclude from "./DeviceExclude/index.vue";

export default {
  name: "DebugTools",
  components: {
    NetworkDebug,
    MonitorDict,
    DeviceExclude
  },
  data() {
    return {
      loadingRobots: true,
      robots: [],
      selectedRobot: null,
      currentTool: null,
      searchQuery: '',
      searchExpanded: false,
      debugTools: [
        {
          key: 'network',
          title: '网络调试',
          description: '查看机器人网络信息和连接状态',
          icon: 'md-wifi',
          badge: 'Debug',
          component: 'NetworkDebug',
          requireRobot: true
        },
        {
          key: 'dict',
          title: '字典管理',
          description: '管理监控字典、错误码和参数配置',
          icon: 'md-bookmarks',
          badge: 'Config',
          component: 'MonitorDict',
          requireRobot: false
        },
        {
          key: 'deviceExclude',
          title: '监控设备排除配置',
          description: '配置机器人未安装的设备，监控时将忽略这些设备',
          icon: 'md-settings',
          badge: 'Config',
          component: 'DeviceExclude',
          requireRobot: true
        }
    ]
    };
  },
  computed: {
    filteredRobots() {
      if (!this.searchQuery) return this.robots;
      
      const query = this.searchQuery.toLowerCase().trim();
      return this.robots.filter(robot => {
        const name = (robot.name || '').toLowerCase();
        const code = (robot.code || '').toLowerCase();
        return name.includes(query) || code.includes(query);
      });
    }
  },
  methods: {
    async loadRobots() {
      this.loadingRobots = true;
      try {
        const res = await api.getData("robot-server/robot/config/listAll", this);
        if (res.state === 1 && res.data) {
          this.robots = res.data.map(robot => ({
            code: robot.code,
            name: robot.name || robot.code,
            tenantName: robot.tenantName//租户名称
          }));
        } else {
          this.$Notice.error({
            title: '提示',
            desc: res.msg || '获取机器人列表失败'
          });
          this.robots = [];
        }
      } catch (error) {
        console.error('加载机器人列表失败:', error);
        this.$Notice.error({
          title: '提示',
          desc: '加载机器人列表失败'
        });
        this.robots = [];
      } finally {
        this.loadingRobots = false;
      }
    },
    selectRobot(robot) {
      this.selectedRobot = robot;
    },
    selectTool(tool) {
      if (tool.requireRobot && !this.selectedRobot) {
        this.$Notice.warning({
          title: '提示',
          desc: '请先选择机器人'
        });
        return;
      }
      this.currentTool = tool;
    },
    backToToolSelection() {
      this.currentTool = null;
    },
    scrollRobots(direction) {
      const container = this.$refs.selectorInner;
      if (container) {
        const scrollAmount = 200;
        if (direction === 'left') {
          container.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
        } else {
          container.scrollBy({ left: scrollAmount, behavior: 'smooth' });
        }
      }
    },
    toggleSearch() {
      this.searchExpanded = !this.searchExpanded;
      if (this.searchExpanded) {
        this.$nextTick(() => {
          this.$refs.searchInput?.focus();
        });
      } else {
        this.searchQuery = '';
      }
    },
    clearSearch() {
      this.searchQuery = '';
      this.$refs.searchInput?.focus();
    },
    handleSearchBlur() {
      // 延迟收缩，给清除按钮时间响应
      setTimeout(() => {
        if (!this.searchQuery) {
          this.searchExpanded = false;
        }
      }, 200);
    }
  },
  mounted() {
    this.loadRobots();
  }
};
</script>

<style scoped lang="less">
@keyframes pulse {
  0% { opacity: 0.5; transform: scale(1); }
  50% { opacity: 1; transform: scale(1.2); }
  100% { opacity: 0.5; transform: scale(1); }
}

@keyframes float {
  0% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
  100% { transform: translateY(0px); }
}

@keyframes rotate {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

@keyframes slideIn {
  from {
    width: 0;
    opacity: 0;
  }
  to {
    width: 200px;
    opacity: 1;
  }
}

.debug-tools-container {
  width: 100%;
  height: 100vh;
  display: flex;
  flex-direction: column;
  background: #050a14;
  color: #00f0ff;
  font-family: 'Orbitron', 'Roboto', sans-serif;
  position: relative;
  overflow: hidden;
  padding: 20px;
  box-sizing: border-box;
}

.cyber-grid {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image: 
    linear-gradient(rgba(0, 240, 255, 0.05) 1px, transparent 1px),
    linear-gradient(90deg, rgba(0, 240, 255, 0.05) 1px, transparent 1px);
  background-size: 40px 40px;
  z-index: 1;
  pointer-events: none;
}

.cyber-glow {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: radial-gradient(circle at 50% 50%, rgba(0, 240, 255, 0.1), transparent 70%);
  z-index: 1;
  pointer-events: none;
}

// 顶部单行布局容器
.top-header-bar {
  display: flex;
  align-items: center;
  gap: 15px;
  margin-bottom: 20px;
  position: relative;
  z-index: 2;
}

.page-header {
  position: relative;
  padding-left: 12px;
  flex-shrink: 0;
  
  .cyber-line {
    position: absolute;
    left: 0;
    top: 50%;
    width: 3px;
    height: 100%;
    background: #00f0ff;
    transform: translateY(-50%);
    box-shadow: 0 0 8px rgba(0, 240, 255, 0.6);
  }
  
  .title-content {
    display: flex;
    flex-direction: column;
  }
  
  .page-title {
    font-size: 16px;
    margin: 0;
    font-weight: 600;
    color: #fff;
    text-shadow: 0 0 8px rgba(0, 240, 255, 0.6);
    letter-spacing: 0.5px;
    line-height: 1.2;
    white-space: nowrap;
  }
  
  .subtitle {
    font-size: 9px;
    color: rgba(0, 240, 255, 0.5);
    letter-spacing: 0.5px;
    text-transform: uppercase;
    margin-top: 2px;
    white-space: nowrap;
  }
}

.tools-section {
  position: relative;
  z-index: 2;
  margin-bottom: 20px;
}

.selected-robot-badge {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: rgba(0, 40, 80, 0.7);
  border: 1px solid rgba(0, 240, 255, 0.5);
  border-radius: 6px;
  font-size: 12px;
  box-shadow: 0 0 8px rgba(0, 240, 255, 0.2);
  white-space: nowrap;
  flex-shrink: 0;
  
  i {
    font-size: 14px;
    color: #00f0ff;
  }
  
  .badge-label {
    color: rgba(0, 240, 255, 0.7);
  }
  
  .badge-value {
    color: #00f0ff;
    font-weight: 600;
    text-shadow: 0 0 5px rgba(0, 240, 255, 0.6);
  }
}

.robot-loading {
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 70px;
}

// 机器人选择器样式
.robot-selection-container {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  
  // 搜索容器
  .search-container {
    display: flex;
    align-items: center;
    gap: 5px;
    transition: all 0.3s ease;
    flex-shrink: 0;
    
    .search-btn {
      width: 40px;
      height: 40px;
      display: flex;
      align-items: center;
      justify-content: center;
      background: linear-gradient(135deg, rgba(0, 40, 80, 0.9), rgba(0, 100, 160, 0.7));
      border: 1px solid rgba(0, 240, 255, 0.6);
      border-radius: 8px;
      color: #00f0ff;
      font-size: 18px;
      cursor: pointer;
      transition: all 0.3s ease;
      box-shadow: 0 0 15px rgba(0, 0, 0, 0.3), inset 0 0 10px rgba(0, 240, 255, 0.2);
      position: relative;
      z-index: 5;
      
      .search-icon {
        position: relative;
        display: block;
        width: 16px;
        height: 16px;
        border: 2px solid #00f0ff;
        border-radius: 50%;
        z-index: 2;
        
        &::after {
          content: '';
          position: absolute;
          width: 2px;
          height: 8px;
          background: #00f0ff;
          bottom: -8px;
          right: -4px;
          transform: rotate(-45deg);
          border-radius: 2px;
        }
      }
      
      &:hover {
        background: linear-gradient(135deg, rgba(0, 60, 100, 0.9), rgba(0, 140, 200, 0.7));
        border-color: rgba(0, 240, 255, 0.9);
        box-shadow: 0 0 20px rgba(0, 0, 0, 0.4), inset 0 0 15px rgba(0, 240, 255, 0.4);
        transform: scale(1.05);
      }
    }
    
    .search-input {
      width: 200px;
      height: 40px;
      padding: 0 12px;
      background: linear-gradient(135deg, rgba(0, 21, 41, 0.95), rgba(0, 40, 80, 0.9));
      border: 1px solid rgba(0, 240, 255, 0.5);
      border-radius: 8px;
      color: #fff;
      font-size: 14px;
      outline: none;
      transition: all 0.3s ease;
      box-shadow: inset 0 0 10px rgba(0, 240, 255, 0.15);
      animation: slideIn 0.3s ease;
      
      &::placeholder {
        color: rgba(0, 240, 255, 0.5);
      }
      
      &:focus {
        border-color: rgba(0, 240, 255, 0.8);
        box-shadow: 0 0 15px rgba(0, 240, 255, 0.3), inset 0 0 10px rgba(0, 240, 255, 0.2);
      }
    }
    
    .clear-btn {
      width: 30px;
      height: 30px;
      display: flex;
      align-items: center;
      justify-content: center;
      background: rgba(255, 45, 45, 0.2);
      border: 1px solid rgba(255, 45, 45, 0.5);
      border-radius: 6px;
      color: #FF2D2D;
      font-size: 14px;
      cursor: pointer;
      transition: all 0.2s ease;
      position: relative;
      z-index: 5;
      
      .close-icon {
        position: relative;
        display: block;
        width: 14px;
        height: 14px;
        
        &::before,
        &::after {
          content: '';
          position: absolute;
          top: 50%;
          left: 50%;
          width: 100%;
          height: 2px;
          background: #FF2D2D;
          border-radius: 1px;
        }
        
        &::before {
          transform: translate(-50%, -50%) rotate(45deg);
        }
        
        &::after {
          transform: translate(-50%, -50%) rotate(-45deg);
        }
      }
      
      &:hover {
        background: rgba(255, 45, 45, 0.3);
        border-color: rgba(255, 45, 45, 0.8);
        transform: scale(1.1);
      }
    }
  }
  
  .nav-btn {
    width: 50px;
    height: 50px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    background: transparent;
    border: none;
    position: relative;
    flex-shrink: 0;
    
    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: linear-gradient(135deg, 
        rgba(0, 40, 80, 0.9), 
        rgba(0, 100, 160, 0.7)
      );
      border-radius: 8px;
      transform: skewX(-10deg);
      border: 1px solid rgba(0, 240, 255, 0.6);
      box-shadow: 0 0 15px rgba(0, 0, 0, 0.3), 
                 inset 0 0 10px rgba(0, 240, 255, 0.2);
      z-index: 1;
    }
    
    .arrow-3d {
      position: relative;
      z-index: 2;
      font-size: 24px;
      color: #00f0ff;
      text-shadow: 0 0 10px rgba(0, 240, 255, 0.8);
      transition: all 0.3s ease;
    }
    
    &:hover::before {
      background: linear-gradient(135deg, 
        rgba(0, 60, 100, 0.9), 
        rgba(0, 140, 200, 0.7)
      );
      border-color: rgba(0, 240, 255, 0.9);
      box-shadow: 0 0 20px rgba(0, 0, 0, 0.4), 
                 inset 0 0 15px rgba(0, 240, 255, 0.4);
    }
    
    &:hover .arrow-3d {
      transform: scale(1.2);
      text-shadow: 0 0 15px rgba(0, 240, 255, 1);
    }
    
    &.next::before {
      transform: skewX(10deg);
    }
  }
}

.robot-selector {
  flex: 1;
  height: 70px;
  display: flex;
  align-items: center;
  position: relative;
  overflow: hidden;
  border-radius: 6px;
  
  .selector-bg {
    position: absolute;
    inset: 0;
    background: linear-gradient(90deg, 
      rgba(0, 21, 41, 0.95), 
      rgba(0, 40, 80, 0.95) 20%, 
      rgba(0, 60, 100, 0.95) 50%, 
      rgba(0, 40, 80, 0.95) 80%, 
      rgba(0, 21, 41, 0.95)
    );
    box-shadow: inset 0 0 20px rgba(0, 240, 255, 0.2),
                0 0 15px rgba(0, 0, 0, 0.5);
    border: 1px solid rgba(0, 240, 255, 0.3);
    z-index: 0;
  }
  
  .selector-inner {
    display: flex;
    align-items: center;
    padding: 10px 15px;
    overflow-x: auto;
    width: 100%;
    scroll-behavior: smooth;
    scrollbar-width: none;
    -ms-overflow-style: none;
    position: relative;
    z-index: 2;
    
    &::-webkit-scrollbar {
      display: none;
    }
  }
}

.robot-item {
  position: relative;
  flex: 0 0 auto;
  min-width: 160px;
  height: 60px;
  margin: 0 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  
  .hexagon-bg {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    clip-path: polygon(5% 0%, 95% 0%, 100% 50%, 95% 100%, 5% 100%, 0% 50%);
    background: linear-gradient(135deg, 
      rgba(0, 21, 41, 0.95), 
      rgba(0, 60, 100, 0.7)
    );
    border: 1px solid rgba(0, 240, 255, 0.4);
    box-shadow: 0 0 10px rgba(0, 0, 0, 0.5), inset 0 0 8px rgba(0, 240, 255, 0.15);
    transition: all 0.3s ease;
    z-index: 1;
  }
  
  .robot-content {
    position: relative;
    z-index: 2;
    display: flex;
    align-items: center;
    height: 100%;
    padding: 0 20px;
  }
  
  &:hover {
    transform: translateY(-3px) scale(1.05);
    
    .hexagon-bg {
      background: linear-gradient(135deg, 
        rgba(0, 30, 60, 0.95), 
        rgba(0, 80, 120, 0.7)
      );
      border-color: rgba(0, 240, 255, 0.6);
      box-shadow: 0 5px 15px rgba(0, 0, 0, 0.6), inset 0 0 10px rgba(0, 240, 255, 0.25);
    }
  }
  
  &.active {
    transform: scale(1.08);
    
    .hexagon-bg {
      background: linear-gradient(135deg, 
        rgba(0, 40, 80, 0.95), 
        rgba(0, 100, 160, 0.8)
      );
      border-color: rgba(0, 240, 255, 0.9);
      box-shadow: 0 8px 20px rgba(0, 0, 0, 0.7), inset 0 0 15px rgba(0, 240, 255, 0.4);
    }
    
    .robot-avatar .robot-icon {
      filter: drop-shadow(0 0 5px rgba(0, 240, 255, 0.8));
      animation: float 4s ease-in-out infinite;
    }
  }
  
  &.online .robot-avatar .status-dot {
    background: #39FF14;
    box-shadow: 0 0 10px rgba(57, 255, 20, 0.8);
  }
  
  &.offline .robot-avatar .status-dot {
    background: #FF2D2D;
    box-shadow: 0 0 10px rgba(255, 45, 45, 0.8);
  }
  
  .robot-avatar {
    position: relative;
    width: 36px;
    height: 36px;
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-right: 10px;
    
    .robot-icon {
      width: 100%;
      height: 100%;
      object-fit: contain;
      z-index: 2;
    }
    
    .status-dot {
      position: absolute;
      bottom: -2px;
      right: -2px;
      width: 8px;
      height: 8px;
      border-radius: 50%;
      animation: pulse 2s infinite;
      z-index: 3;
    }
  }
  
  .robot-info {
    flex: 1;
    
    .robot-name {
      color: #fff;
      font-size: 14px;
      font-weight: 600;
      display: block;
      margin-bottom: 4px;
    }
    
    .status-line {
      display: flex;
      gap: 8px;
      font-size: 11px;
      
      .robot-code {
        color: #00f0ff;
        font-family: 'Courier New', monospace;
      }
      
      .robot-status {
        color: rgba(255, 255, 255, 0.7);
      }
    }
  }
}

// 功能卡片网格
.function-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 30px;
}

.function-card {
  position: relative;
  background: rgba(0, 20, 40, 0.7);
  border: 1px solid rgba(0, 240, 255, 0.3);
  border-radius: 12px;
  padding: 30px;
  cursor: pointer;
  transition: all 0.4s ease;
  overflow: hidden;
  
  &.disabled {
    opacity: 0.5;
    cursor: not-allowed;
    
    &:hover {
      transform: none;
      border-color: rgba(0, 240, 255, 0.3);
      box-shadow: none;
    }
  }
  
  .card-bg {
    position: absolute;
    inset: 0;
    background: linear-gradient(135deg, rgba(0, 21, 41, 0.8), rgba(0, 40, 80, 0.6));
    z-index: 0;
    transition: all 0.3s ease;
  }
  
  .card-content {
    position: relative;
    z-index: 2;
    text-align: center;
  }
  
  .function-icon {
    width: 80px;
    height: 80px;
    margin: 0 auto 20px;
    background: rgba(0, 240, 255, 0.1);
    border: 2px solid rgba(0, 240, 255, 0.5);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.3s ease;
    
    i {
      font-size: 40px;
      color: #00f0ff;
      text-shadow: 0 0 10px rgba(0, 240, 255, 0.8);
    }
  }
  
  .function-title {
    font-size: 22px;
    font-weight: 600;
    color: #fff;
    margin: 0 0 12px 0;
    text-shadow: 0 0 8px rgba(0, 240, 255, 0.5);
  }
  
  .function-desc {
    font-size: 14px;
    color: rgba(255, 255, 255, 0.7);
    line-height: 1.6;
    margin: 0;
  }
  
  .function-badge {
    position: absolute;
    top: 15px;
    right: 15px;
    padding: 4px 12px;
    background: rgba(0, 240, 255, 0.2);
    border: 1px solid rgba(0, 240, 255, 0.5);
    border-radius: 12px;
    font-size: 12px;
    color: #00f0ff;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 1px;
  }
  
  .require-robot-tip {
    margin-top: 12px;
    padding: 8px 12px;
    background: rgba(255, 152, 0, 0.2);
    border: 1px solid rgba(255, 152, 0, 0.5);
    border-radius: 6px;
    font-size: 12px;
    color: #FF9800;
  }
  
  .card-corner {
    position: absolute;
    width: 20px;
    height: 20px;
    border: 2px solid rgba(0, 240, 255, 0.5);
    transition: all 0.3s ease;
    
    &.top-right {
      top: 0;
      right: 0;
      border-left: none;
      border-bottom: none;
    }
    
    &.bottom-left {
      bottom: 0;
      left: 0;
      border-right: none;
      border-top: none;
    }
  }
  
  &:hover:not(.disabled) {
    transform: translateY(-10px);
    border-color: #00f0ff;
    box-shadow: 0 15px 40px rgba(0, 0, 0, 0.7), 0 0 30px rgba(0, 240, 255, 0.5);
    
    .card-bg {
      background: linear-gradient(135deg, rgba(0, 30, 60, 0.9), rgba(0, 60, 100, 0.7));
    }
    
    .function-icon {
      background: rgba(0, 240, 255, 0.2);
      border-color: #00f0ff;
      box-shadow: 0 0 20px rgba(0, 240, 255, 0.6);
      transform: scale(1.1);
    }
    
    .card-corner {
      border-width: 3px;
      border-color: #00f0ff;
      box-shadow: 0 0 15px rgba(0, 240, 255, 0.8);
    }
  }
}

// 工具显示区域
.tool-display-section {
  position: relative;
  z-index: 2;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  
  .tool-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 10px 16px;
    background: rgba(0, 20, 40, 0.6);
    border: 1px solid rgba(0, 240, 255, 0.3);
    border-radius: 6px;
    margin-bottom: 15px;
    
    .tool-title {
      display: flex;
      align-items: center;
      gap: 10px;
      font-size: 18px;
      font-weight: 600;
      color: #fff;
      
      i {
        font-size: 20px;
        color: #00f0ff;
        text-shadow: 0 0 8px rgba(0, 240, 255, 0.6);
      }
    }
    
    .back-btn {
      background: rgba(0, 20, 40, 0.6);
      border: 1px solid rgba(0, 240, 255, 0.3);
      color: rgba(255, 255, 255, 0.8);
      transition: all 0.3s ease;
      height: 36px;
      padding: 0 16px;
      
      &:hover {
        border-color: rgba(0, 240, 255, 0.6);
        background: rgba(0, 40, 80, 0.7);
        box-shadow: 0 0 15px rgba(0, 240, 255, 0.3);
      }
    }
  }
  
  .tool-content {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }
}

// 加载器样式
.cyber-loader {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
}

.cyber-loader-inner {
  width: 80px;
  height: 80px;
  border: 3px solid transparent;
  border-top: 3px solid rgba(0, 240, 255, 0.8);
  border-right: 3px solid rgba(0, 240, 255, 0.6);
  border-radius: 50%;
  animation: rotate 1s linear infinite;
  box-shadow: 0 0 20px rgba(0, 240, 255, 0.5);
  margin-bottom: 15px;
}

.cyber-loader-text {
  color: #00f0ff;
  text-transform: uppercase;
  letter-spacing: 2px;
  font-size: 16px;
  text-shadow: 0 0 10px rgba(0, 240, 255, 0.8);
}

// 角落装饰
.corner-decoration {
  position: absolute;
  width: 100px;
  height: 100px;
  pointer-events: none;
  z-index: 3;
  
  &.top-left {
    top: 0;
    left: 0;
    border-top: 2px solid rgba(0, 240, 255, 0.5);
    border-left: 2px solid rgba(0, 240, 255, 0.5);
    box-shadow: -5px -5px 15px rgba(0, 240, 255, 0.3);
  }
  
  &.top-right {
    top: 0;
    right: 0;
    border-top: 2px solid rgba(0, 240, 255, 0.5);
    border-right: 2px solid rgba(0, 240, 255, 0.5);
    box-shadow: 5px -5px 15px rgba(0, 240, 255, 0.3);
  }
  
  &.bottom-left {
    bottom: 0;
    left: 0;
    border-bottom: 2px solid rgba(0, 240, 255, 0.5);
    border-left: 2px solid rgba(0, 240, 255, 0.5);
    box-shadow: -5px 5px 15px rgba(0, 240, 255, 0.3);
  }
  
  &.bottom-right {
    bottom: 0;
    right: 0;
    border-bottom: 2px solid rgba(0, 240, 255, 0.5);
    border-right: 2px solid rgba(0, 240, 255, 0.5);
    box-shadow: 5px 5px 15px rgba(0, 240, 255, 0.3);
  }
}
</style>
